/* eslint-disable @typescript-eslint/no-unsafe-return */
import type { ChangeEvent } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
	IconMagnifierOutline18 as SearchIcon,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import type { FeedbackSearchResult } from "@productlane/zero/queries"
import {
	Button,
	Empty,
	IconButton,
	Input,
	ScrollArea,
	Virtualizer,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { EmptySearchIllustration } from "@/components/empty-search-illustration"
import { MetaInfo } from "@/components/meta-info"
import { SearchListItem } from "@/components/search-list-item"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { useBackNavigation, useSetBackNavigation } from "@/lib/back-navigation"
import { isQuerySettled } from "@/lib/query-settled"
import { sortSearchThreads } from "@/lib/sort-search-threads"
import { useSortState } from "@/lib/states/global-state"

type SearchResultItem =
	| {
			id: string
			workspaceId: string
			text: string
			title: string | null
			person: string | null
			company: string | null
			_formatted:
				| {
						text: string
						title: string | null
						person: string | null
						company: string | null
				  }
				| null
				| undefined
	  }
	| {
			id: string
			text: string
			threadId: string
			workspaceId: string
			_formatted:
				| {
						text: string
				  }
				| null
				| undefined
	  }

type SearchResults = SearchResultItem[] | null | undefined

export const SearchScreen = () => {
	const navigate = useNavigate()
	const query = useSearch({ strict: false, select: (s) => s.q }) || ""
	const [searchTerm, setSearchTerm] = useState(query)
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
	const { goBack } = useBackNavigation()
	const [searchResults, setSearchResults] = useState<SearchResults>([])
	const [offset, setOffset] = useState(0)
	const [isHydrated, setIsHydrated] = useState(false)
	const [isMoreData, setIsMoreData] = useState(true)

	useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm])

	useSetBackNavigation(
		{
			type: "search",
			url: `/search?q=${query}`,
		},
		[query],
	)

	//* note that limit is equal 80 actually, since we have email, threads and slack documents in meilisearch
	//* the limit will increase as we add new documents to search through
	const { isFetching, refetch } = api.workspace.searchResults.useQuery(
		{ query: debouncedSearchTerm, limit: 20, offset },
		{
			cacheTime: 5000,
			staleTime: 5000,
			refetchOnMount: "always",
			onSuccess: (newData) => {
				if (newData?.length === 0 || !newData) {
					setIsMoreData(false)
				}
				if (newData && !isHydrated) {
					//@ts-expect-error types error mismatch because of _formatted that meilisearch returns
					setSearchResults((prev) => [...prev, ...newData])
					setOffset((prevOffset) => prevOffset + newData.length)
					setIsHydrated(true)
				}
			},
		},
	)

	const threadIds = useMemo(() => {
		if (!searchResults) return []
		const threadIds = new Set(
			searchResults.reduce<string[]>(
				(acc, result) =>
					"threadId" in result
						? [...acc, result.threadId]
						: [...acc, result.id],
				[],
			),
		)
		return Array.from(threadIds)
	}, [searchResults])

	const [filteredThreads, filteredThreadsResult] = useQuery(
		queries.findSearchedThreads({ threadIds }),
	)

	const sortByQueryColumn = useSortState((s) => s.sortByQueryColumn)
	const sortByQueryColumnOrder = useSortState((s) => s.sortByQueryColumnOrder)

	const searchItemsWithThreads = useMemo(() => {
		if (!searchResults) return []
		const withThread: {
			item: SearchResultItem
			thread: FeedbackSearchResult
		}[] = []

		for (const item of searchResults) {
			const thread = filteredThreads?.find(({ id }) => item.id === id)

			if (thread) {
				withThread.push({ item, thread })
			}
		}

		return sortSearchThreads({
			items: withThread,
			getThread: ({ thread }) => thread,
			column: sortByQueryColumn,
			order: sortByQueryColumnOrder,
		})
	}, [
		searchResults,
		filteredThreads,
		sortByQueryColumn,
		sortByQueryColumnOrder,
	])

	useShortcut(
		"esc",
		() => {
			goBack("/threads")
		},
		{
			preventDefault: true,
			enableOnContentEditable: true,
			enableOnFormTags: ["INPUT"],
		},
	)

	const content = useMemo(
		() => (
			<Virtualizer
				itemsData={searchItemsWithThreads}
				itemContent={({ item, thread }) => (
					<SearchListItem
						thread={thread}
						{...(item?._formatted && "person" in item._formatted
							? { contactName: item._formatted.person }
							: {})}
						{...(item?._formatted && "company" in item._formatted
							? { companyName: item._formatted.company }
							: {})}
						{...(item?._formatted && "title" in item._formatted
							? { title: item._formatted.title }
							: {})}
						text={item?._formatted?.text}
					/>
				)}
			>
				<div className="m-6 flex w-full items-center justify-center">
					{isMoreData ? (
						<Button
							className="p-4"
							variant="outline"
							onClick={async () => {
								setIsHydrated(false)
								await refetch()
							}}
							disabled={isFetching}
						>
							Load more
						</Button>
					) : (
						<div className="select-none text-sm font-normal text-tertiary">
							No more data to search...
						</div>
					)}
				</div>
			</Virtualizer>
		),
		[searchItemsWithThreads, isFetching, isMoreData, refetch],
	)

	const onChange = useCallback(
		(e: ChangeEvent<{ value: string }>) => setSearchTerm(e.target.value),
		[],
	)

	const onClick = useCallback(() => setSearchTerm(""), [])

	useEffect(() => {
		if (searchTerm.startsWith("/") && searchTerm.length === 1) setSearchTerm("")
	}, [searchTerm])

	useEffect(() => {
		void navigate({
			to: ".",
			search: searchTerm ? { q: searchTerm } : {},
			replace: true,
		})
	}, [searchTerm, navigate])

	useEffect(() => {
		setSearchResults([])
		setOffset(0)
		setIsHydrated(false)
		setIsMoreData(true)
	}, [debouncedSearchTerm])

	return (
		<>
			<MetaInfo title="Search" />

			<div className="sticky top-0 z-1 mr-4 flex h-12 min-h-12 w-full flex-row items-center justify-between border-b bg-white pl-12 pr-2 md:pl-[calc(17.5px+var(--nav-toggle-inset,0px))] md:pr-3 dark:bg-gray-900">
				<Input
					variant="borderless"
					name="searchTerm"
					value={searchTerm}
					onChange={onChange}
					icon={SearchIcon}
					iconClassName="text-primary left-3"
					inputClassName="indent-8 pr-10"
					placeholder="Search workspace"
					autoFocus
				/>
				{!!searchTerm.length && (
					<IconButton Icon={X} variant="ghost" onClick={onClick} />
				)}
			</div>

			{searchItemsWithThreads?.length ? (
				<div className="min-h-0 flex-1">
					<ScrollArea>{content}</ScrollArea>
				</div>
			) : isFetching ||
			  (threadIds.length > 0 && !isQuerySettled(filteredThreadsResult)) ? (
				<div></div>
			) : (
				<div className="flex min-h-0 flex-1 justify-center">
					<Empty
						text="Try changing the search parameters"
						title="Nothing was found"
						image={<EmptySearchIllustration />}
					/>
				</div>
			)}
		</>
	)
}
